import { useMemo, useState, useTransition, type ReactElement } from "react";
import { t } from "@lingui/core/macro";
import { Temporal } from "temporal-polyfill";
import { ColumnDef } from "@tanstack/react-table";

import { TextField } from "_/components/text-field";
import { Select, StylesConfig } from "_/components/select";
import { Table } from "_/components/table";
import { Button } from "_/components/button";
import {
  TelemetryEventKind,
  EventQuery,
  Machine,
  StateChangeEventData,
  TelemetryEvent,
  ThermalsEventData,
  useMachineEvents,
} from "_/data/machines";
import { Badge } from "_/components/badge";
import { DateTime } from "_/components/date-time";

import * as S from "./styled";
import { formatTemperature } from "_/utils";

type MachineEventsProps = {
  machine: Machine;
};

// Constructs translated label for event kinds.
function translatedLabel(kind: string): string {
  return t`common.event-kinds ${kind}`;
}

// Helper for rendering the event "kind" in each table row.
const RenderKind = ({ event }: { event: TelemetryEvent }) => {
  const kind = event.kind;
  const label = translatedLabel(kind);

  switch (kind) {
    case "thermals":
      return <Badge kind="warning">{label}</Badge>;
    case "stateChange":
      return <Badge kind="info">{label}</Badge>;
    case "systemProfile":
      return <Badge kind="highlight">{label}</Badge>;
    case "startup":
      return <Badge kind="success">{label}</Badge>;
    case "systemInfo":
      return <Badge kind="success">{label}</Badge>;
    case "snapshot":
      return <Badge kind="info">{label}</Badge>;
    default:
      return <Badge kind="muted">Unknown ({kind})</Badge>;
  }
};

// Helper for rendering event data in each table row.
const RenderData = ({ event }: { event: TelemetryEvent }) => {
  function renderThermals(data: ThermalsEventData) {
    const temps = Object.keys(data).map((k, i) => {
      const val = data[k];
      return (
        <S.ThermalsReading key={i}>
          <S.SensorLabel>{k}:</S.SensorLabel>
          <S.SensorTemp>
            {formatTemperature({ value: val.temperature, kind: "current" })}
          </S.SensorTemp>
          <S.SensorTarget>
            /{formatTemperature({ value: val.target, kind: "target" })}
          </S.SensorTarget>
        </S.ThermalsReading>
      );
    });

    return <S.ThermalDataWrapper>{temps}</S.ThermalDataWrapper>;
  }

  function renderStateChange(data: StateChangeEventData) {
    return (
      <S.StateChange>
        <S.RawJson>{JSON.stringify(data, null, 2)}</S.RawJson>
      </S.StateChange>
    );
  }

  // TODO: Add prettier output for startup data and profiles.
  function renderDefault(data: unknown) {
    return <S.RawJson>{JSON.stringify(data, null, 2)}</S.RawJson>;
  }

  switch (event.kind) {
    case "thermals":
      return renderThermals(event.data);
    case "stateChange":
      return renderStateChange(event.data);
    case "startup":
      return renderDefault(event.data);
    case "systemInfo":
      return renderDefault(event.data);
    case "snapshot":
      return renderDefault(event.data);
    case "systemProfile":
      return renderDefault(event.data);
    default:
      // In case new event kinds show up in the server before the client
      return renderDefault((event as TelemetryEvent).data);
  }
};

const RenderTimestamp = ({ event }: { event: TelemetryEvent }) => {
  const ts = new Date(event.timestamp / 1_000);

  return (
    <span title={ts.toISOString()}>
      <DateTime value={ts} precision="millisecond" />
    </span>
  );
};

type QueryParams = Omit<EventQuery, "machineId">;

const FilterBar = ({
  queryParams,
  onChange,
}: {
  queryParams: QueryParams;
  onChange: (params: QueryParams) => void;
}) => {
  const [params, setParams] = useState<QueryParams>(queryParams);

  // Change handler for the select input.
  function selectChange(vs: Array<TelemetryEventKind | "all">) {
    const kind = vs.indexOf("all") > 0 ? [] : vs.filter((v) => v !== "all");
    setParams((p) => ({ ...p, kind }));
    onChange({ ...params, kind });
  }

  const kindOptions: Array<{
    value: TelemetryEventKind | "all";
    label: string;
  }> = [
    { value: "all", label: t`common.all` },
    { value: "thermals", label: translatedLabel("thermals") },
    { value: "stateChange", label: translatedLabel("stateChange") },
    { value: "startup", label: translatedLabel("startup") },
    { value: "systemInfo", label: translatedLabel("systemInfo") },
    { value: "systemProfile", label: translatedLabel("systemProfile") },
  ];

  const styles: StylesConfig<(typeof kindOptions)[number]> = {
    multiValueLabel: (base, state) => {
      return state.data.value === "all"
        ? { ...base, padding: "3px 6px" }
        : base;
    },
    multiValueRemove: (base, state) => {
      return state.data.value === "all" ? { ...base, display: "none" } : base;
    },
  };

  return (
    <S.FilterBar>
      <S.FilterBarItem>
        <S.Label>{t`components.machine-events.kind`}:</S.Label>
        <Select
          options={kindOptions}
          value={params.kind?.length ? params.kind : ["all"]}
          isClearable={false}
          styles={styles}
          onChange={selectChange}
          multiple
        />
      </S.FilterBarItem>
      <S.FilterBarItem>
        <S.Label>{t`components.machine-events.limit`}:</S.Label>
        <TextField
          type="number"
          value={params.limit}
          min={1}
          max={1000}
          onChange={(value) =>
            setParams((p) => ({
              ...p,
              limit: typeof value === "number" ? value : undefined,
            }))
          }
        />
      </S.FilterBarItem>
      <Button size="small" onClick={() => onChange(params)}>
        {t`components.machine-events.run-query`}
      </Button>
    </S.FilterBar>
  );
};

const getTableColumns = (): ColumnDef<TelemetryEvent>[] => [
  {
    header: () => t`common.event-time`,
    accessorKey: "timestamp",
    cell: ({ row: { original: rowData } }) => (
      <RenderTimestamp event={rowData} />
    ),
  },
  {
    header: () => t`common.type`,
    accessorKey: "kind",
    cell: ({ row: { original: rowData } }) => <RenderKind event={rowData} />,
  },
  {
    header: () => t`common.data`,
    accessorKey: "data",
    cell: ({ row: { original: rowData } }) => <RenderData event={rowData} />,
  },
];

export const MachineEvents = ({
  machine,
}: MachineEventsProps): ReactElement => {
  // Keep cell components mounted when only the machine's last-seen time changes.
  const tableColumns = useMemo(() => getTableColumns(), []);

  // Show events from a week before we last saw the machine
  // Note that these number values are in microseconds.
  const lastSeen = machine.lastSeen
    ? Temporal.Instant.from(machine.lastSeen)
    : undefined;
  const from = lastSeen
    ? lastSeen.subtract({ hours: 7 * 24 }).epochMilliseconds * 1000
    : undefined;
  const to = lastSeen
    ? lastSeen.add({ minutes: 10 }).epochMilliseconds * 1000
    : undefined;

  const [_, startTransition] = useTransition();
  const [queryParams, setQueryParams] = useState<QueryParams>({
    from,
    to,
    limit: 50,
    order: "desc",

    // Default to showing only stateChange, startup, and systemInfo events.
    // Thermals events are very frequent and are usually not
    // interesting to the user. Snapshot events are interesting, but they are
    // already displayed elsewhere in machine detail. Log and systemProfile
    // events are, AFAICT, not used.
    kind: ["stateChange", "startup", "systemInfo"] as TelemetryEventKind[],
  });

  const {
    data: events = [],
    isLoading,
    refetch,
  } = useMachineEvents(
    {
      machineId: machine.id,
      ...queryParams,
    },
    {
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
    }
  );

  function runQuery(params: QueryParams) {
    const kinds = params.kind ?? [];
    const currentKinds = queryParams.kind ?? [];
    const paramsUnchanged =
      from === queryParams.from &&
      to === queryParams.to &&
      params.limit === queryParams.limit &&
      params.order === queryParams.order &&
      kinds.length === currentKinds.length &&
      kinds.every((kind, index) => kind === currentKinds[index]);

    // Advance the time window only when the user runs the query.
    startTransition(() => setQueryParams({ ...params, from, to }));
    if (paramsUnchanged) {
      void refetch();
    }
  }

  return (
    <S.Wrapper>
      <FilterBar queryParams={queryParams} onChange={runQuery} />
      <S.Content>
        <Table columns={tableColumns} data={events} loading={isLoading} />
      </S.Content>
    </S.Wrapper>
  );
};
